import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PDFViewer } from '../components/PDFViewer'
import { createLocalizedPath } from '../utils/i18n'

export function Home() {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedPDF, setSelectedPDF] = useState<File | null>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files) as File[]
    const pdfFiles = files.filter((file: File) => file.type === 'application/pdf')
    
    if (pdfFiles.length > 0) {
      setSelectedPDF(pdfFiles[0]) // Load the first PDF
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[]
    const pdfFiles = files.filter((file: File) => file.type === 'application/pdf')
    
    if (pdfFiles.length > 0) {
      setSelectedPDF(pdfFiles[0]) // Load the first PDF
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary-600">BlinkPDF</h1>
              <span className="ml-2 text-sm text-gray-500">PDF Tools in Your Browser</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Process PDFs Locally in Your Browser
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Secure, fast, and private PDF processing. Select pages to keep, split documents, and more.
            Your files never leave your device.
          </p>
        </div>

        {/* File Selection Area or PDF Viewer */}
        {!selectedPDF ? (
          /* File Selection Area */
          <div className="max-w-2xl mx-auto mb-12">
            <div
              className={`
                border-2 border-dashed rounded-lg p-12 text-center transition-colors duration-200
                ${isDragOver 
                  ? 'border-primary-400 bg-primary-50' 
                  : 'border-gray-300 hover:border-gray-400'
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Drop your PDF files here
                </h3>
                <p className="text-gray-600">
                  or{' '}
                  <label className="text-primary-600 hover:text-primary-700 cursor-pointer font-medium">
                    browse to select
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf"
                      multiple
                      onChange={handleFileSelect}
                    />
                  </label>
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Processed locally in your browser
              </p>
            </div>
          </div>
        ) : (
          /* PDF Viewer with Thumbnails */
          <div className="max-w-6xl mx-auto mb-12">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedPDF.name}
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSelectedPDF(null)}
                  className="btn-secondary text-sm"
                >
                  ← Select Another PDF
                </button>
              </div>
            </div>
            <PDFViewer 
              file={selectedPDF}
            />
          </div>
        )}

        {/* Features Grid - only show when no PDF is loaded */}
        {!selectedPDF && (
          <>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="card text-center">
                <div className="mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">100% Private</h3>
                <p className="text-gray-600">
                  All processing happens locally in your browser. Your files never leave your device.
                </p>
              </div>

              <div className="card text-center">
                <div className="mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-gray-600">
                  You don't need to upload or download files.
                </p>
              </div>

              <div className="card text-center">
                <div className="mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Installation</h3>
                <p className="text-gray-600">
                  Works directly in your browser. No downloads, no setup, no registration required.
                </p>
              </div>
            </div>

            {/* SEO Content Section */}
            <section className="mt-16 max-w-4xl mx-auto">
              <div className="bg-gray-50 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Professional PDF Page Management Tool
                </h2>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Split PDF Documents Online
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Extract specific pages from PDF files with our browser-based PDF splitter. 
                      Select individual pages or page ranges to create new PDF documents instantly.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Remove unwanted pages from PDF files</li>
                      <li>• Extract chapters or sections</li>
                      <li>• Create custom PDF compilations</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Secure & Private PDF Processing
                    </h3>
                    <p className="text-gray-600 mb-4">
                      All PDF processing happens locally in your browser. Your documents never 
                      leave your device, ensuring complete privacy and security.
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• No file uploads to external servers</li>
                      <li>• Client-side PDF manipulation</li>
                      <li>• GDPR compliant processing</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                    How to Split PDF Pages
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div>
                      <div className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <span className="text-primary-600 font-semibold">1</span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">Upload PDF</h4>
                      <p className="text-sm text-gray-600">Click to select or drag your PDF file</p>
                    </div>
                    <div>
                      <div className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <span className="text-primary-600 font-semibold">2</span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">Select Pages</h4>
                      <p className="text-sm text-gray-600">Choose which pages to keep or remove</p>
                    </div>
                    <div>
                      <div className="bg-primary-100 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2">
                        <span className="text-primary-600 font-semibold">3</span>
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">Download</h4>
                      <p className="text-sm text-gray-600">Get your processed PDF instantly</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mt-16 max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    How do I split a PDF into separate pages?
                  </h3>
                  <p className="text-gray-600">
                    Upload your PDF file, review the page thumbnails, uncheck the pages you want to remove, 
                    then click "Download PDF" to get a new file with only your selected pages.
                  </p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Is it safe to upload PDF files to BlinkPDF?
                  </h3>
                  <p className="text-gray-600">
                    Yes, completely safe. All PDF processing happens locally in your browser. 
                    Your files are never uploaded to our servers or stored anywhere online.
                  </p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    What file size limit does BlinkPDF have?
                  </h3>
                  <p className="text-gray-600">
                    Since processing happens in your browser, the limit depends on your device's memory. 
                    Most PDFs up to 100MB work smoothly on modern computers.
                  </p>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Can I merge PDF files with BlinkPDF?
                  </h3>
                  <p className="text-gray-600">
                    Currently, BlinkPDF focuses on page selection and removal from individual PDF files. 
                    PDF merging features may be added in future updates.
                  </p>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p>&copy; 2025 BlinkPDF. Built for privacy and performance.</p>
            <p className="mt-2 text-sm">
              <Link to={createLocalizedPath('/about')} className="text-primary-600 hover:text-primary-700 font-medium">
                About & Terms
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
